import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import ReferralCard from "@/components/ReferralCard";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Sparkles, Trash2, FileText, Loader2, Crown, Zap } from "lucide-react";
import { PLAN_TIERS } from "@/lib/plans";

export default function Dashboard() {
  const { user, quota, refresh } = useAuth();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const { data } = await api.get("/projects");
        setProjects(data.projects);
      } catch {
        toast.error("Impossible de charger les projets");
      } finally {
        setLoading(false);
      }
    })();
  }, [user]);

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce projet ?")) return;
    try {
      await api.delete(`/projects/${id}`);
      setProjects(projects.filter((p) => p.id !== id));
      await refresh();
      toast.success("Projet supprimé");
    } catch {
      toast.error("Suppression impossible");
    }
  };

  const percent = quota ? Math.min(100, (quota.used / Math.max(1, quota.quota)) * 100) : 0;
  const upgradeTier = quota?.plan === "premium" ? null : PLAN_TIERS.find((t) => (quota?.plan === "free" ? t.key === "starter" : quota?.plan === "starter" ? t.key === "pro" : t.key === "premium"));

  return (
    <div className="min-h-screen">
      <Navbar />
      <main className="pt-24 pb-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="dashboard-main">
        <div className="flex flex-wrap items-end justify-between gap-6 mb-10 animate-fade-up">
          <div>
            <div className="text-xs font-mono uppercase tracking-widest text-indigo-400">Tableau de bord</div>
            <h1 className="text-3xl sm:text-4xl font-bold mt-2">Bonjour {user?.name} 👋</h1>
            <p className="text-gray-400 mt-2">Prêt à générer votre prochain SaaS ?</p>
          </div>
          <button onClick={() => navigate("/generer")} className="pill-primary flex items-center gap-2" data-testid="dashboard-new-saas-button">
            <Plus size={16} /> Nouveau SaaS
          </button>
        </div>

        {/* Quota Card */}
        <div className="grid md:grid-cols-3 gap-6 mb-10">
          <div className="glass rounded-2xl p-6 md:col-span-2" data-testid="quota-card">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs font-mono uppercase tracking-widest text-gray-400">Forfait actuel</div>
                <div className="mt-2 flex items-center gap-2">
                  <span className="text-2xl font-bold capitalize">{quota?.plan_name || "Découverte"}</span>
                  {quota?.plan === "premium" && <Crown size={20} className="text-amber-400" />}
                </div>
              </div>
              <div className="text-right">
                <div className="text-xs font-mono uppercase tracking-widest text-gray-400">Ce mois-ci</div>
                <div className="text-2xl font-bold mt-2" data-testid="quota-count">
                  {quota?.used || 0} <span className="text-gray-500 text-lg">/ {quota?.quota || 0}</span>
                </div>
              </div>
            </div>
            <div className="mt-5 h-2 rounded-full bg-white/5 overflow-hidden">
              <div
                className="h-full rounded-full bg-gradient-to-r from-indigo-500 to-cyan-400 transition-all duration-700"
                style={{ width: `${percent}%` }}
              />
            </div>
            <div className="mt-3 text-xs text-gray-500">
              SaaS générés dans le cycle mensuel
              {quota?.bonus_credits > 0 && <span className="text-emerald-300"> · dont +{quota.bonus_credits} bonus parrainage</span>}
            </div>
          </div>

          {upgradeTier && (
            <div className="rounded-2xl p-6 bg-gradient-to-br from-indigo-500/20 to-cyan-500/10 border border-indigo-500/30 card-hover" data-testid="upgrade-card">
              <div className="flex items-center gap-2 text-amber-300">
                <Zap size={16} />
                <span className="text-xs font-mono uppercase tracking-widest">Booster</span>
              </div>
              <h3 className="mt-3 text-lg font-semibold">Passez à {upgradeTier.name}</h3>
              <p className="text-sm text-gray-300 mt-2">{upgradeTier.quota}, dès {upgradeTier.price} €/mois.</p>
              <button onClick={() => navigate("/tarifs")} className="mt-4 pill-primary text-sm" data-testid="upgrade-button">
                Améliorer mon forfait
              </button>
            </div>
          )}
        </div>

        <ReferralCard />

        {/* Projects list */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Vos SaaS générés</h2>
          <div className="text-xs font-mono uppercase tracking-widest text-gray-500">{projects.length} projet{projects.length > 1 ? 's' : ''}</div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-24 text-gray-400"><Loader2 className="animate-spin" /></div>
        ) : projects.length === 0 ? (
          <div className="glass rounded-2xl p-12 text-center" data-testid="empty-projects">
            <div className="w-16 h-16 mx-auto rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center">
              <Sparkles size={24} className="text-indigo-400" />
            </div>
            <h3 className="mt-6 text-lg font-semibold">Aucun SaaS pour l'instant</h3>
            <p className="text-sm text-gray-400 mt-2">Décrivez votre idée et laissez l'IA construire le blueprint.</p>
            <button onClick={() => navigate("/generer")} className="mt-6 pill-primary" data-testid="empty-cta">
              Générer mon premier SaaS
            </button>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((p) => (
              <div key={p.id} className="glass card-hover rounded-2xl p-6" data-testid={`project-card-${p.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: p.blueprint?.color_palette?.primary || '#6366F1', boxShadow: `0 8px 20px -8px ${p.blueprint?.color_palette?.primary || '#6366F1'}` }}>
                    <FileText size={18} className="text-white" />
                  </div>
                  <button onClick={() => remove(p.id)} className="p-2 rounded-full hover:bg-red-500/10 text-gray-500 hover:text-red-400 transition-colors" data-testid={`delete-project-${p.id}`} aria-label="Supprimer">
                    <Trash2 size={16} />
                  </button>
                </div>
                <h3 className="mt-4 text-lg font-semibold truncate">{p.blueprint?.name || "SaaS sans nom"}</h3>
                <p className="mt-2 text-sm text-gray-400 line-clamp-2">{p.blueprint?.tagline || p.description}</p>
                <div className="mt-4 flex items-center justify-between text-xs">
                  <span className="font-mono text-gray-500">{new Date(p.created_at).toLocaleDateString('fr-FR')}</span>
                  <Link to={`/projet/${p.id}`} className="text-indigo-400 hover:text-indigo-300 font-medium" data-testid={`view-project-${p.id}`}>
                    Voir le blueprint →
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
